import type {
  ParkingPlaceCreateApiData,
  ParkingPlaceCreateApiResponses,
  ParkingPlaceDeleteApiData,
  ParkingPlaceDeleteApiResponses,
  ParkingPlaceEntranceCreateApiData,
  ParkingPlaceEntranceCreateApiResponses,
  ParkingPlaceEntranceDeleteApiData,
  ParkingPlaceEntranceDeleteApiResponses,
  ParkingPlaceEntranceGetApiData,
  ParkingPlaceEntranceGetApiResponses,
  ParkingPlaceEntranceListApiData,
  ParkingPlaceEntranceListApiResponses,
  ParkingPlaceEntrancePatchApiData,
  ParkingPlaceEntrancePatchApiResponses,
  ParkingPlaceGetApiData,
  ParkingPlaceGetApiResponses,
  ParkingPlaceListApiData,
  ParkingPlaceListApiResponses,
  ParkingPlaceMinimalListApiData,
  ParkingPlaceMinimalListApiResponses,
  ParkingPlacePatchApiData,
  ParkingPlacePatchApiResponses,
  ParkingZoneCreateApiData,
  ParkingZoneCreateApiResponses,
  ParkingZoneDeleteApiData,
  ParkingZoneDeleteApiResponses,
  ParkingZoneGetApiData,
  ParkingZoneGetApiResponses,
  ParkingZoneListApiData,
  ParkingZoneListApiResponses,
  ParkingZonePatchApiData,
  ParkingZonePatchApiResponses
} from '@navio/nsdk';

import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';

export const getParkingPlaceUrls = {
  getPaginatedParkingPlaces: dataEndpoint<ParkingPlaceListApiData, ParkingPlaceListApiResponses>({
    getUrl: (queryParams) => apiUrl('parking-places/', queryParams)
  }),
  getPaginatedMinimalParkingPlaces: dataEndpoint<ParkingPlaceMinimalListApiData, ParkingPlaceMinimalListApiResponses>({
    getUrl: (queryParams) => apiUrl('parking-places/minimal/', queryParams)
  }),
  getParkingPlace: dataEndpoint<ParkingPlaceGetApiData, ParkingPlaceGetApiResponses>({
    getUrl: ({ parkingPlaceId }) => apiUrl(`parking-places/${parkingPlaceId}/`)
  }),
  postParkingPlace: responseEndpoint<ParkingPlaceCreateApiData, ParkingPlaceCreateApiResponses>({
    getUrl: () => apiUrl('parking-places/'),
    getBody: (data) => data
  }),
  patchParkingPlace: responseEndpoint<ParkingPlacePatchApiData, ParkingPlacePatchApiResponses>({
    getUrl: ({ parkingPlaceId }) => apiUrl(`parking-places/${parkingPlaceId}/`),
    getBody: ({ parkingPlaceId: _parkingPlaceId, ...body }) => body
  }),
  deleteParkingPlace: dataEndpoint<ParkingPlaceDeleteApiData, ParkingPlaceDeleteApiResponses>({
    getUrl: ({ parkingPlaceId }) => apiUrl(`parking-places/${parkingPlaceId}/`)
  })
};

/**
 * Entrance writes carry a `File` when the image is set or replaced, so the body cannot go through
 * the camel→snake transformation (it would mangle the FormData). Both branches hand-roll the
 * snake_case wire keys napi multipart expects; `image: null` (delete) rides JSON, since multipart
 * cannot express null.
 */
type ParkingPlaceEntranceWireInput = {
  parkingPlaceId?: number;
  name?: string | null;
  status?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  heightLimitCm?: number | null;
  widthLimitCm?: number | null;
  instructions?: string | null;
  image?: Blob | File | null;
};

const entranceHeaders = (data: ParkingPlaceEntranceWireInput): Record<string, string> =>
  data.image instanceof Blob ? { 'Content-Type': 'multipart/form-data' } : { 'Content-Type': 'application/json' };

const toParkingPlaceEntranceBody = (data: ParkingPlaceEntranceWireInput): FormData | Record<string, unknown> => {
  if (data.image instanceof Blob) {
    const formData = new FormData();
    if (data.parkingPlaceId != null) {
      formData.append('parking_place_id', String(data.parkingPlaceId));
    }
    if (data.name != null) {
      formData.append('name', data.name);
    }
    if (data.status != null) {
      formData.append('status', data.status);
    }
    if (data.latitude != null) {
      formData.append('latitude', String(data.latitude));
    }
    if (data.longitude != null) {
      formData.append('longitude', String(data.longitude));
    }
    if (data.heightLimitCm != null) {
      formData.append('height_limit_cm', String(data.heightLimitCm));
    }
    if (data.widthLimitCm != null) {
      formData.append('width_limit_cm', String(data.widthLimitCm));
    }
    if (data.instructions != null) {
      formData.append('instructions', data.instructions);
    }
    formData.append('image', data.image);
    return formData;
  }
  return {
    ...(data.parkingPlaceId !== undefined ? { parking_place_id: data.parkingPlaceId } : {}),
    ...(data.name !== undefined ? { name: data.name } : {}),
    ...(data.status !== undefined ? { status: data.status } : {}),
    ...(data.latitude !== undefined ? { latitude: data.latitude } : {}),
    ...(data.longitude !== undefined ? { longitude: data.longitude } : {}),
    ...(data.heightLimitCm !== undefined ? { height_limit_cm: data.heightLimitCm } : {}),
    ...(data.widthLimitCm !== undefined ? { width_limit_cm: data.widthLimitCm } : {}),
    ...(data.instructions !== undefined ? { instructions: data.instructions } : {}),
    ...(data.image === null ? { image: null } : {})
  };
};

export const getParkingPlaceEntranceUrls = {
  getPaginatedParkingPlaceEntrances: dataEndpoint<
    ParkingPlaceEntranceListApiData,
    ParkingPlaceEntranceListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('parking-place-entrances/', queryParams)
  }),
  getParkingPlaceEntrance: dataEndpoint<ParkingPlaceEntranceGetApiData, ParkingPlaceEntranceGetApiResponses>({
    getUrl: ({ parkingPlaceEntranceId }) => apiUrl(`parking-place-entrances/${parkingPlaceEntranceId}/`)
  }),
  postParkingPlaceEntrance: responseEndpoint<ParkingPlaceEntranceCreateApiData, ParkingPlaceEntranceCreateApiResponses>(
    {
      getUrl: () => apiUrl('parking-place-entrances/'),
      getHeaders: entranceHeaders,
      getBody: toParkingPlaceEntranceBody,
      skipBodyTransformation: true
    }
  ),
  patchParkingPlaceEntrance: responseEndpoint<ParkingPlaceEntrancePatchApiData, ParkingPlaceEntrancePatchApiResponses>({
    getUrl: ({ parkingPlaceEntranceId }) => apiUrl(`parking-place-entrances/${parkingPlaceEntranceId}/`),
    getHeaders: entranceHeaders,
    getBody: toParkingPlaceEntranceBody,
    skipBodyTransformation: true
  }),
  deleteParkingPlaceEntrance: dataEndpoint<ParkingPlaceEntranceDeleteApiData, ParkingPlaceEntranceDeleteApiResponses>({
    getUrl: ({ parkingPlaceEntranceId }) => apiUrl(`parking-place-entrances/${parkingPlaceEntranceId}/`)
  })
};

export const getParkingZoneUrls = {
  getPaginatedParkingZones: dataEndpoint<ParkingZoneListApiData, ParkingZoneListApiResponses>({
    getUrl: (queryParams) => apiUrl('parking-zones/', queryParams)
  }),
  getParkingZone: dataEndpoint<ParkingZoneGetApiData, ParkingZoneGetApiResponses>({
    getUrl: ({ parkingZoneId }) => apiUrl(`parking-zones/${parkingZoneId}/`)
  }),
  postParkingZone: responseEndpoint<ParkingZoneCreateApiData, ParkingZoneCreateApiResponses>({
    getUrl: () => apiUrl('parking-zones/'),
    getBody: (data) => data
  }),
  patchParkingZone: responseEndpoint<ParkingZonePatchApiData, ParkingZonePatchApiResponses>({
    getUrl: ({ parkingZoneId }) => apiUrl(`parking-zones/${parkingZoneId}/`),
    getBody: ({ parkingZoneId: _parkingZoneId, ...body }) => body
  }),
  deleteParkingZone: dataEndpoint<ParkingZoneDeleteApiData, ParkingZoneDeleteApiResponses>({
    getUrl: ({ parkingZoneId }) => apiUrl(`parking-zones/${parkingZoneId}/`)
  })
};
